import { useEffect, useState, type ReactNode } from 'react';
import { wireKbps } from './wire';
import { useQuery } from '@tanstack/react-query';
import { get, type Ingest, type InputView, type StreamKey } from '../api';
import { INPUT_REASON } from './adapter';
import { ObsConnection } from './ObsConnection';
import { srtOffer, useObsProtocol, type ObsProtocol } from './srt';
import { PrimaryButton } from './ui';
import { Chip, Icon } from '../ui';
import { StepFooter, StepHeading, linkBtn, type StepProps } from './SetupWizard';
import { audioLabel, audioRate, videoLabel } from './setup';
import { ObsPluginHint, RelayLinkHint } from './AppsHints';
import { IngestRegionPicker } from './IngestRegion';

/** No stream for this long: suggest Relay Link (docs/apps-downloads.md 3.5, 6). */
const RELAY_LINK_AFTER_S = 45;

/** Input reasons the guide explains in the TRY IT block. */
const BLOCKING = ['codec_unsupported', 'bitrate_over_limit', 'key_revoked', 'stream_suspended', 'account_blocked'] as const;

interface Chip {
  text: string;
  /** set when the stream is outside the recommendation: amber chip with this hint */
  warn?: string;
}

/** Recommended settings, each checked against the stream once it arrives. */
function chips(input: InputView | undefined, ingest: Ingest | undefined): Chip[] {
  const live = !!input?.live;
  const gop = input?.gop_seconds ?? 0;
  const kbps = input ? wireKbps(input) : 0;
  const limit = ingest?.max_bitrate_kbps;
  const rate = audioRate(input?.audio_format);
  const aac = !input?.audio_format || /AAC/i.test(input.audio_format);
  return [
    { text: 'H.264' },
    { text: 'CBR 4.5–6 Mbps', warn: live && limit && kbps > limit ? `yours ${(kbps / 1000).toFixed(1)} Mbps, limit ${(limit / 1000).toFixed(1)}` : undefined },
    { text: 'Keyframe 2 s', warn: live && gop > 2.05 ? `yours ${gop.toFixed(1)} s` : undefined },
    {
      text: 'AAC 48 kHz',
      warn: live && !aac ? `yours ${input!.audio_format}` : live && rate && rate !== 48000 ? `yours ${(rate / 1000).toFixed(1)} kHz` : undefined,
    },
  ];
}

/** Step 1: server and stream key for OBS, and a live check that the stream arrives. */
export function SetupStepObs({ view, dests, headingRef, go }: StepProps) {
  const ingest = useQuery({ queryKey: ['ingest'], queryFn: () => get<Ingest>('/api/ingest') });
  const keys = useQuery({ queryKey: ['stream-keys'], queryFn: () => get<StreamKey[]>('/api/stream-keys') });
  const key = keys.data?.find((k) => !k.revoked_at) ?? keys.data?.[0];
  const input = view?.inputs.find((i) => i.live) ?? view?.inputs[0];
  const live = !!input?.live && view?.node !== 'unreachable';
  const reason = input?.state_reason && (BLOCKING as readonly string[]).includes(input.state_reason) ? input.state_reason : undefined;
  const list = chips(input, ingest.data);
  const waited = useWaiting(live);
  const [chosen, setProto] = useObsProtocol(input?.protocol);
  // What the panel below actually shows (SRT falls back to RTMPS where it isn't offered).
  const shownProto: ObsProtocol = chosen === 'srt' && srtOffer(ingest.data, key).address ? 'srt' : 'rtmps';

  return (
    <>
      <div className="grid gap-6 p-4 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] md:gap-10 md:p-7">
        <div className="flex min-w-0 flex-col gap-4">
          <StepHeading headingRef={headingRef} sub={<SameHeight testId="setup-obs-sub" show={shownProto === 'srt' ? 0 : 1}>{[
            <>
              <span className="md:hidden">OBS → Settings → Stream → Custom. Paste the address, leave the key empty.</span>
              <span className="hidden md:inline">In OBS open <b className="text-text">Settings → Stream</b>, set Service to <b className="text-text">Custom</b>, paste the address into Server and leave Stream Key empty.</span>
            </>,
            <>
              <span className="md:hidden">OBS → Settings → Stream → Custom. Paste these two.</span>
              <span className="hidden md:inline">In OBS open <b className="text-text">Settings → Stream</b>, set Service to <b className="text-text">Custom</b> and paste these two.</span>
            </>,
          ]}</SameHeight>}>
            Connect OBS
          </StepHeading>
          <IngestRegionPicker ingest={ingest.data} />
          <ObsConnection ingest={ingest.data} streamKey={key} proto={chosen} onProto={setProto} />
          <div className="flex flex-col gap-1.5">
            <span className="text-xs text-muted">Recommended in OBS → Output</span>
            <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
              {list.map((c) => (
                <li key={c.text} data-warn={c.warn ? 'true' : undefined}
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-[3px] font-mono text-xs ${c.warn ? 'border-dashed border-warn/60 text-warn' : 'border-line text-text-soft'}`}>
                  {c.warn && <Icon name="state-warn" size={12} />}{c.text}{c.warn && <span className="font-sans text-[11px]"> · {c.warn}</span>}
                </li>
              ))}
            </ul>
          </div>
          <ObsPluginHint variant="card" />
        </div>

        <div className="flex min-w-0 flex-col gap-3">
          <span className="sx-label">Try it</span>
          <StreamState input={input} live={live} reason={reason} waited={waited} armed={(dests ?? []).some((d) => d.enabled && !d.needs_key)} srt={shownProto === 'srt'} />
        </div>
      </div>

      <StepFooter
        left={<button type="button" data-testid="setup-skip" className={linkBtn} onClick={() => go(2)}>Skip for now</button>}
        hint={live ? undefined : 'Confirm once we see your stream'}
        primary={
          <PrimaryButton testId="setup-confirm" className="max-md:min-h-[44px]" disabled={!live} onClick={() => go(2)}>
            Confirm and continue
          </PrimaryButton>
        }
      />
    </>
  );
}

/** Seconds since the step started listening; restarts whenever the stream goes away. */
function useWaiting(live: boolean): number {
  const [since, setSince] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (live) return;
    const t0 = Date.now();
    setSince(t0);
    setNow(t0);
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live]);
  return live ? 0 : Math.max(0, Math.floor((now - since) / 1000));
}

const clock = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/**
 * Texts that change with the protocol, laid in one grid cell: the cell keeps the height of the
 * longest, so switching RTMPS ⇄ SRT moves nothing below it (owner, 18.10, as the OBS fields).
 * Only `show` is visible and read out; the others hold their place, invisible.
 */
function SameHeight({ show, children, testId }: { show: number; children: ReactNode[]; testId?: string }) {
  return (
    <span className="grid [&>*]:col-start-1 [&>*]:row-start-1" data-testid={testId} data-show={show}>
      {children.map((c, i) => (
        <span key={i} className={i === show ? '' : 'invisible'} aria-hidden={i === show ? undefined : true}>{c}</span>
      ))}
    </span>
  );
}

function StreamState({ input, live, reason, armed, waited, srt }: { input?: InputView; live: boolean; reason?: keyof typeof INPUT_REASON; armed: boolean; waited: number; srt: boolean }) {
  if (live && input) {
    const kbps = wireKbps(input);
    const cells = [
      // The bitrate comes with the 5 s sample, not with the input event.
      { l: 'Bitrate', v: kbps > 0 ? `${(kbps / 1000).toFixed(1)} Mbps` : '—', t: kbps > 0 ? undefined : 'measuring…' },
      { l: 'Video', v: videoLabel(input.resolution, input.fps || input.declared_fps) ?? '—' },
      { l: 'Keyframe', v: input.gop_seconds ? `${input.gop_seconds.toFixed(1)} s` : '—' },
      { l: 'Audio', v: audioLabel(input.audio_format) ?? '—' },
    ];
    return (
      <div role="status" data-testid="setup-stream-state" data-state="live" className="flex flex-col gap-3 rounded-lg border border-live/45 bg-bg p-4 md:p-[18px]">
        <div className="flex items-center gap-2.5">
          <Chip state="live">WE SEE YOUR STREAM</Chip>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {cells.map((c) => (
            <div key={c.l} className="min-w-0">
              <div className="text-[11px] text-muted">{c.l}</div>
              <div className="tabular truncate font-mono text-lg font-medium">{c.v}</div>
              {c.t && <div className="text-[11px] text-muted">{c.t}</div>}
            </div>
          ))}
        </div>
        {reason ? (
          <span className="flex items-center gap-1.5 text-[13px] text-warn"><Icon name="state-warn" size={12} />{INPUT_REASON[reason]}.</span>
        ) : (
          <span className="text-[13px] text-text-soft">Looks good. You can stop OBS now or keep it running.</span>
        )}
      </div>
    );
  }
  return (
    <div role="status" data-testid="setup-stream-state" data-state="listening" className="flex flex-col gap-2.5 rounded-lg border border-dashed border-line-2 p-4 md:p-[18px]">
      <div className="flex items-center gap-2.5">
        {/* still: the Waiting logo in the header is the view's one heartbeat (07, AUDIT WZ-04) */}
        <Icon name="state-start" size={12} className="text-start" />
        <span className="font-mono text-xs font-medium tracking-[.08em] text-start">
          LISTENING FOR YOUR STREAM…<span aria-hidden data-testid="setup-waited" className="tabular"> {clock(waited)}</span>
        </span>
      </div>
      {reason ? (
        <span className="flex items-center gap-1.5 text-sm text-fail"><Icon name="state-fail" size={12} />{INPUT_REASON[reason]}.</span>
      ) : (
        <span className="text-sm text-text-soft">
          Press <b className="text-text">Start Streaming</b> in OBS.{' '}
          {armed ? 'Destinations that are on and have a key go live too.' : 'Nothing goes public yet: no destinations are connected.'}
        </span>
      )}
      <span className="text-xs text-muted">
        <SameHeight testId="setup-obs-hint" show={srt ? 0 : 1}>{[
          <>Not showing up? Check that the whole SRT address is in Server and Stream Key is empty. Some networks block UDP: try RTMPS then.</>,
          <>Not showing up? Check that the server ends with <span className="font-mono">/live</span> and the key has no spaces.</>,
        ]}</SameHeight>
      </span>
      {!reason && waited >= RELAY_LINK_AFTER_S && <RelayLinkHint />}
    </div>
  );
}
